import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Button } from "./ui/button";
import { Check, ChevronsUpDown } from "lucide-react";

/**
 * Searchable item picker. Accepts a list of {id, sku, name, unit, quantity}.
 * User can type to filter and pick from the list. Fires onChange(itemId).
 */
export default function ItemCombobox({
  items = [],
  value,
  onChange,
  placeholder = "Type or pick an item…",
  testId = "item-combobox",
  showStock = true,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const selected = items.find((i) => i.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={`${testId}-trigger`}
          className="w-full justify-between rounded-sm border-slate-300 font-normal h-10"
        >
          <span className="truncate text-left">
            {selected ? (
              <>
                <span className="font-mono text-xs mr-2 text-slate-500">{selected.sku}</span>
                {selected.name}
              </>
            ) : (
              <span className="text-slate-400">{placeholder}</span>
            )}
          </span>
          <ChevronsUpDown size={14} className="ml-2 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <Command
          filter={(itemValue, search) => {
            // itemValue holds the sku|name concatenation we set in each CommandItem
            const q = (search || "").toLowerCase();
            return itemValue.toLowerCase().includes(q) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Type SKU or name…" data-testid={`${testId}-search`} className="h-10" />
          <CommandList>
            <CommandEmpty>No items match your search.</CommandEmpty>
            <CommandGroup>
              {items.map((i) => (
                <CommandItem
                  key={i.id}
                  value={`${i.sku} ${i.name}`}
                  onSelect={() => { onChange(i.id); setOpen(false); }}
                  data-testid={`${testId}-option-${i.id}`}
                  className="cursor-pointer"
                >
                  <Check size={14} className={`mr-2 ${value === i.id ? "opacity-100 text-blue-800" : "opacity-0"}`} />
                  <div className="flex items-center justify-between w-full gap-3 min-w-0">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-900 truncate">{i.name}</div>
                      <div className="text-[11px] font-mono text-slate-500 truncate">{i.sku}</div>
                    </div>
                    {showStock && (
                      <div className="text-right shrink-0">
                        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">stock</div>
                        <div className="text-xs font-mono font-semibold text-slate-900">
                          {i.quantity}<span className="text-slate-400"> {i.unit}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
